import { supabase } from '../config/supabase';
import { SystemSettings, Webhook, SystemSetting, ApiResponse } from '../types';

export class SettingsService {
  static async getSettings(): Promise<ApiResponse<SystemSettings>> {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('*');

      if (error) {
        return { data: null, error: error.message };
      }

      const settings: SystemSettings = {
        notifications: {
          email: true,
          sms: false,
          newMatches: true,
          projectUpdates: true,
          engineerUpdates: false,
        },
        api: {
          lineToken: '',
          lineSecret: '',
          openaiKey: '',
          claudeKey: '',
        },
        matching: {
          threshold: 80,
          autoSend: false,
          requireAllSkills: false,
          locationWeight: 30,
          skillWeight: 70,
        },
        dashboardDisplay: {
          showMonthlyRevenue: true,
          showConversionRate: true,
          showMatchingAccuracy: true,
        },
      };

      if (data) {
        data.forEach((setting: SystemSetting) => {
          switch (setting.setting_key) {
            case 'notifications':
              if (typeof setting.setting_value === 'object' && setting.setting_value !== null) {
                settings.notifications = { ...settings.notifications, ...(setting.setting_value as Record<string, unknown>) };
              }
              break;
            case 'api':
              if (typeof setting.setting_value === 'object' && setting.setting_value !== null) {
                settings.api = { ...settings.api, ...(setting.setting_value as Record<string, unknown>) };
              }
              break;
            case 'matching':
              if (typeof setting.setting_value === 'object' && setting.setting_value !== null) {
                settings.matching = { ...settings.matching, ...(setting.setting_value as Record<string, unknown>) };
              }
              break;
            case 'dashboardDisplay':
              if (typeof setting.setting_value === 'object' && setting.setting_value !== null) {
                settings.dashboardDisplay = { ...settings.dashboardDisplay, ...(setting.setting_value as Record<string, unknown>) };
              }
              break;
          }
        });
      }

      return { data: settings, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('設定読み込みエラー:', error);
      return {
        data: null,
        error: `設定の読み込みに失敗しました: ${errorMessage}`
      };
    }
  }

  static async saveSettings(settings: SystemSettings): Promise<ApiResponse<boolean>> {
    try {
      const settingsToSave = [
        {
          setting_key: 'notifications',
          setting_value: settings.notifications,
          description: 'Notification preferences',
        },
        {
          setting_key: 'api',
          setting_value: settings.api,
          description: 'API keys and configuration',
        },
        {
          setting_key: 'matching',
          setting_value: settings.matching,
          description: 'Matching algorithm settings',
        },
        {
          setting_key: 'dashboardDisplay',
          setting_value: settings.dashboardDisplay,
          description: 'Dashboard display preferences',
        },
      ];

      for (const setting of settingsToSave) {
        const { error } = await supabase
          .from('system_settings')
          .upsert(setting, {
            onConflict: 'setting_key',
          });

        if (error) {
          return { data: null, error: error.message };
        }
      }

      return { data: true, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('設定保存エラー:', error);
      return {
        data: null,
        error: `設定の保存に失敗しました: ${errorMessage}`
      };
    }
  }

  static async getWebhooks(): Promise<ApiResponse<Webhook[]>> {
    try {
      const { data, error } = await supabase
        .from('webhooks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: data || [], error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Webhook読み込みエラー:', error);
      return { data: null, error: `Webhookの読み込みに失敗しました: ${errorMessage}` };
    }
  }

  static async createWebhook(webhook: Omit<Webhook, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<Webhook>> {
    try {
      const { data, error } = await supabase
        .from('webhooks')
        .insert([webhook])
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Webhook作成エラー:', error);
      return { data: null, error: `Webhookの作成に失敗しました: ${errorMessage}` };
    }
  }

  static async updateWebhook(id: number, webhook: Partial<Webhook>): Promise<ApiResponse<Webhook>> {
    try {
      const { data, error } = await supabase
        .from('webhooks')
        .update(webhook)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Webhook更新エラー:', error);
      return { data: null, error: `Webhookの更新に失敗しました: ${errorMessage}` };
    }
  }

  static async deleteWebhook(id: number): Promise<ApiResponse<boolean>> {
    try {
      const { error } = await supabase
        .from('webhooks')
        .delete()
        .eq('id', id);

      if (error) {
        return { data: null, error: error.message };
      }

      return { data: true, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Webhook削除エラー:', error);
      return { data: null, error: `Webhookの削除に失敗しました: ${errorMessage}` };
    }
  }

  static async testWebhook(webhook: Webhook): Promise<ApiResponse<boolean>> {
    try {
      const testPayload = {
        event: 'test',
        timestamp: new Date().toISOString(),
        data: {
          message: 'This is a test webhook from SES Matching System',
        },
      };

      const response = await fetch(webhook.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(webhook.headers || {}),
        },
        body: JSON.stringify(testPayload),
      });

      if (!response.ok) {
        return { data: null, error: 'Webhook接続に失敗しました' };
      }

      return { data: true, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Webhookテストエラー:', error);
      return { data: null, error: `Webhookテストに失敗しました: ${errorMessage}` };
    }
  }

  static async testApiConnection(apiType: 'line' | 'openai' | 'claude', credentials: any): Promise<ApiResponse<boolean>> {
    try {
      switch (apiType) {
        case 'line':
          if (!credentials.lineToken) {
            return { data: null, error: 'LINE access token is required' };
          }
          
          const lineResponse = await fetch('https://api.line.me/v2/bot/info', {
            headers: {
              'Authorization': `Bearer ${credentials.lineToken}`,
            },
          });
          
          if (!lineResponse.ok) {
            return { data: null, error: 'API接続に失敗しました' };
          }
          break;

        case 'openai':
          if (!credentials.openaiKey) {
            return { data: null, error: 'OpenAI API key is required' };
          }
          
          const openaiResponse = await fetch('https://api.openai.com/v1/models', {
            headers: {
              'Authorization': `Bearer ${credentials.openaiKey}`,
            },
          });
          
          if (!openaiResponse.ok) {
            return { data: null, error: 'API接続に失敗しました' };
          }
          break;

        case 'claude':
          if (!credentials.claudeKey) {
            return { data: null, error: 'Claude API key is required' };
          }
          
          return { data: true, error: null };

        default:
          return { data: null, error: 'サポートされていないAPIタイプです' };
      }

      return { data: true, error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('API接続テストエラー:', error);
      return { data: null, error: `API接続テストに失敗しました: ${errorMessage}` };
    }
  }
}